import React, {useEffect, useRef} from 'react';
import SockJS from 'sockjs-client';
import Stomp from 'stompjs';
import s from './styles.module.css';
import * as env from "../../../constants";

const TimerReceiver = ({judgeStatus, timers, setTimers, selectedCommand, setOpenDialogCustom, setCoachTypeId, setCoachTypeNumber}) => {

    const client = useRef(null);
    const socket = useRef(null);
    const reconnectTimeout = useRef(null);
    const selectedCommandRef = useRef(selectedCommand);

    useEffect(() => {
        selectedCommandRef.current = selectedCommand;
    }, [selectedCommand]);

    useEffect(() => {
        const connect = () => {
            socket.current = new SockJS(`${env.REACT_APP_SYNCHRO_SOCKET_COACH_URL}`);
            client.current = Stomp.over(socket.current);
            client.current.debug = null;
            client.current.heartbeat.outgoing = 1000;
            client.current.heartbeat.incoming = 1000;

            client.onopen = () => {
                if (reconnectTimeout.current) {
                    clearTimeout(reconnectTimeout.current);
                    reconnectTimeout.current = null;
                }
            }

            client.onclose = () => {
                reconnect();
            }

            client.onerror = (error) => {
                console.error('WebSocket ошибка: ', error);
            }

            client.current.connect({}, (frame) => {

                if (reconnectTimeout.current) {
                    clearTimeout(reconnectTimeout.current);
                    reconnectTimeout.current = null;
                }

                console.log('Connected to WebSocket (TimerReceiver).');
                client.current.subscribe('/topic/timers', (message) => {
                    const update = JSON.parse(message.body);
                    const {
                        competitionApplicationDisciplineId,
                        coachTypeNumber,
                        entry,
                        deck,
                        routine,
                        isEntryRunning,
                        isDeckRunning,
                        isRoutineRunning,
                        wasEntryStarted,
                        wasDeckStarted,
                        wasRoutineStarted,
                        isEntryOut,
                        isDeckOut,
                        isRoutineOut
                    } = update;

                    if (competitionApplicationDisciplineId !== selectedCommandRef.current) {
                        return;
                    }

                    // Обновляем только для coachTypeNumber 1, 2 или 3
                    if ([1, 2, 3].includes(coachTypeNumber)) {
                        setTimers((prevTimers) => ({
                            ...prevTimers,
                            [coachTypeNumber]: {
                                entry,
                                deck,
                                routine,
                                isEntryRunning,
                                isDeckRunning,
                                isRoutineRunning,
                                wasEntryStarted,
                                wasDeckStarted,
                                wasRoutineStarted,
                                isEntryOut,
                                isDeckOut,
                                isRoutineOut
                            },
                        }));
                    }
                });
            }, (error) => {
                console.error('WebSocket error:', error);
                reconnect();
            });
        }

        const reconnect = () => {

            if (document.visibilityState === 'hidden') {
                return;
            }

            reconnectTimeout.current = setTimeout(() => {
                connect();
            }, 2000);
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
                    connect();
                }
            } else {
                if (reconnectTimeout.current) {
                    clearTimeout(reconnectTimeout.current);
                    reconnectTimeout.current = null;
                }
            }
        }

        document.addEventListener("visibilitychange", handleVisibilityChange);

        connect();

        return () => {
            if (client.current && client.current.connected) {
                client.current.disconnect(() => {
                    console.log('Disconnected from WebSocket (TimerReceiver)!');
                });
            }
        };
    }, []);

    const formatTime = (time) => {
        const totalSeconds = Math.floor(time);
        const hundredths = Math.round((time % 1) * 100);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${hundredths.toString().padStart(2, '0')}`;
    };

    const getTimerBackground = (isRunning, wasStarted, isOut) => {
        return isOut ? "#E9695E" : isRunning ? '#ebc810' : wasStarted ? '#86b760' : '#ffffff';
    };

    const getTimerColor = (isRunning, wasStarted) => {
        return isRunning ? '#494747' : wasStarted ? '#494747' : '#494747';
    };

    return (
        <div className={s.container}>
            {['1', '2', '3'].map((coachTypeNumber) => (
                <div key={coachTypeNumber} className={s.coach_block}>
                    <div className={s.timer_block}>
                        <div className={s.timekeeper_label}>
                            <h2 onDoubleClick={() => {
                                setOpenDialogCustom(true)
                                setCoachTypeId(6)
                                setCoachTypeNumber(coachTypeNumber)
                            }}
                                className={
                                    judgeStatus?.coach_type_6?.find((f) => f.coach_type_number === Number(coachTypeNumber))?.status === 'disconnect' ? s.disconnect : s.connect
                                }>TK {coachTypeNumber}</h2>
                        </div>
                        <div className={s.timers_container}>
                            <div className={s.timer}>
                                <h3>Entry</h3>
                                <div
                                    className={s.timer_text}
                                    style={{
                                        backgroundColor: getTimerBackground(
                                            timers[coachTypeNumber].isEntryRunning,
                                            timers[coachTypeNumber].wasEntryStarted,
                                            timers[coachTypeNumber].isEntryOut
                                        ),
                                        color: getTimerColor(timers[coachTypeNumber].isEntryRunning, timers[coachTypeNumber].wasEntryStarted),
                                    }}
                                >
                                    <p>{formatTime(timers[coachTypeNumber].entry)}</p>
                                </div>
                            </div>
                            <div className={s.timer}>
                                <h3>Deck</h3>
                                <div
                                    className={s.timer_text}
                                    style={{
                                        backgroundColor: getTimerBackground(
                                            timers[coachTypeNumber].isDeckRunning,
                                            timers[coachTypeNumber].wasDeckStarted,
                                            timers[coachTypeNumber].isDeckOut
                                        ),
                                        color: getTimerColor(timers[coachTypeNumber].isDeckRunning, timers[coachTypeNumber].wasDeckStarted),
                                    }}
                                >
                                    <p>{formatTime(timers[coachTypeNumber].deck)}</p>
                                </div>
                            </div>
                            <div className={s.timer}>
                                <h3>Routine</h3>
                                <div
                                    className={s.timer_text}
                                    style={{
                                        backgroundColor: getTimerBackground(
                                            timers[coachTypeNumber].isRoutineRunning,
                                            timers[coachTypeNumber].wasRoutineStarted,
                                            timers[coachTypeNumber].isRoutineOut
                                        ),
                                        color: getTimerColor(timers[coachTypeNumber].isRoutineRunning, timers[coachTypeNumber].wasRoutineStarted),
                                    }}
                                >
                                    <p>{formatTime(timers[coachTypeNumber].routine)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default TimerReceiver;